/**
 * Minimal IndexedDB wrapper — the app's local cache for study content.
 *
 * One database holds every locally-cached resource; each resource lives in its
 * own object store keyed by its `id`. Adding a resource in a later step is two
 * lines: a new name in `STORES` and a matching `ensureStore()` call in the upgrade
 * handler (bump `DB_VERSION`). The generic get/getAll/put/remove helpers then work
 * for that store unchanged, and `ensureStore` handles both a fresh database and an
 * upgrade of an existing one — including adding an index to a store that is
 * already there.
 */

const DB_NAME = 'sprintspeak.study';
const DB_VERSION = 8;

/**
 * Object store names — single source of truth.
 *
 * Two families live here, and the split is what makes a backup possible: the
 * **content cache** the API fills (`chapters`, `topics_head`, `topics`,
 * `phrases`, binaries and all) versus the **user's own data**, which no endpoint
 * can hand back — `favorites`, `dictionary` and `topics_done`. The latter three
 * are id-shaped rows with no binaries, so they serialize to JSON as they are (see
 * `study/userData.js`).
 */
export const STORES = {
  chapters: 'chapters',
  /**
   * Lightweight per-topic rows (basic info from `/study/chapter/{id}`), plus the
   * cached thumbnail and the local `needUpdate` flag. Indexed by `chapterId` so
   * one chapter's topics can be read without touching the others' images (see
   * `getAllBy`) — and so their ids can be counted without reading any of them
   * (see `getAllKeysBy`).
   */
  topicsHead: 'topics_head',
  /** Full topic content from `/study/topic/{id}`, incl. downloaded `audio_blob`. */
  topics: 'topics',
  /** Favorited topics — `{ id }` rows only; everything else is read from `topics_head`. */
  favorites: 'favorites',
  /**
   * Completed topics — `{ id, doneAt }` rows, the *only* record that a topic was
   * finished. Deliberately not a flag on `topics_head`: completion is the user's
   * data, the head row is the API's, and mixing the two meant a content sync had
   * to remember to carry the flag across. Chapter percentages are derived from
   * this store on read (`study/progress.js`), never stored.
   */
  topicsDone: 'topics_done',
  /**
   * One row per phrase of a topic's levels, with its own downloaded `audio_blob`
   * — the topic's `audio` map covers dialogue sentences only. Indexed by
   * `topicId` so a topic's phrases can be read (and purged) without scanning
   * every cached clip.
   */
  phrases: 'phrases',
  /** Phrases saved to the dictionary — `{ id, addedAt }` rows; content is read from `phrases`. */
  dictionary: 'dictionary',
};

let dbPromise = null;

/**
 * Make sure a store exists and carries the named indexes, whether this is a fresh
 * database or an upgrade of one that already has the store.
 *
 * The distinction matters: `createObjectStore` is only available for a store that
 * isn't there yet, so adding an index to an **existing** store has to go through
 * the upgrade transaction instead. IndexedDB backfills a new index from the rows
 * already in the store, so nothing has to be re-downloaded.
 *
 * @param {IDBDatabase} db
 * @param {IDBTransaction} transaction The upgrade transaction (`request.transaction`).
 * @param {string} name Store name.
 * @param {Array<string>} [indexes] Index names, each on the same-named property.
 */
function ensureStore(db, transaction, name, indexes = []) {
  const store = db.objectStoreNames.contains(name)
    ? transaction.objectStore(name)
    : db.createObjectStore(name, { keyPath: 'id' }); // every store is keyed by `id`

  for (const index of indexes) {
    if (!store.indexNames.contains(index)) store.createIndex(index, index);
  }
}

/** Open the database once and reuse the connection. */
function openDb() {
  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    // Runs on first open and whenever DB_VERSION increases — create/upgrade stores here.
    request.onupgradeneeded = () => {
      const db = request.result;
      const transaction = request.transaction;
      ensureStore(db, transaction, STORES.chapters);
      // Indexed by chapter: every read is "the topics of this chapter", and these
      // rows carry the cached thumbnails, so a full scan would pull every topic's
      // image into memory to answer a question about one chapter.
      ensureStore(db, transaction, STORES.topicsHead, ['chapterId']);
      ensureStore(db, transaction, STORES.topics);
      ensureStore(db, transaction, STORES.favorites);
      // Added in v8. Starts empty on purpose: the completion flags older caches
      // carry on their `topics_head` rows are deliberately *not* migrated, so a
      // pre-v8 install re-earns its checks by listening to the lessons again.
      ensureStore(db, transaction, STORES.topicsDone);
      // Indexed by topic: same reasoning, for the audio these rows carry.
      ensureStore(db, transaction, STORES.phrases, ['topicId']);
      ensureStore(db, transaction, STORES.dictionary);
    };

    request.onsuccess = () => {
      const db = request.result;
      // A delete (or version upgrade) requested elsewhere — another tab, or the
      // account-deletion path below — is blocked for as long as any connection
      // stays open. Close ours when asked and drop the memoised promise, so the
      // next read reopens instead of reusing a dead handle.
      db.onversionchange = () => {
        db.close();
        dbPromise = null;
      };
      resolve(db);
    };
    request.onerror = () => reject(request.error);
  });

  return dbPromise;
}

/**
 * Drop the whole database: every cached chapter, topic, audio clip and thumbnail,
 * and the user's own rows — favorites, dictionary and completed topics — with it.
 *
 * This is the account-deletion path (see `auth/session.js`), not the sign-out
 * one — a logout deliberately keeps this cache, since none of it is tied to the
 * token, but a deleted account must leave nothing on the device.
 *
 * Our own connection has to be closed first: IndexedDB refuses to delete while a
 * connection is open, and a blocked request never fires `onsuccess`. We still
 * resolve on `blocked` — a connection in another tab that ignores
 * `onversionchange` must not leave the UI waiting forever; the delete then
 * completes as soon as that tab lets go.
 */
export async function deleteDatabase() {
  const opening = dbPromise;
  dbPromise = null;
  // `catch` covers "never opened" and "failed to open" — nothing to close then.
  await Promise.resolve(opening)
    .then((db) => db?.close())
    .catch(() => {});

  return new Promise((resolve, reject) => {
    const request = indexedDB.deleteDatabase(DB_NAME);
    request.onsuccess = () => resolve();
    request.onblocked = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

/** Run `op(store)` inside a transaction and resolve with the request's result. */
function run(storeName, mode, op) {
  return openDb().then(
    (db) =>
      new Promise((resolve, reject) => {
        const request = op(db.transaction(storeName, mode).objectStore(storeName));
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      })
  );
}

/** All records in a store (unordered). */
export function getAll(storeName) {
  return run(storeName, 'readonly', (store) => store.getAll()).then((rows) => rows || []);
}

/**
 * All records whose indexed field equals `key` (unordered).
 *
 * Lets a store be read by something other than its key without pulling every
 * row into memory first — which matters for rows carrying audio binaries.
 */
export function getAllBy(storeName, indexName, key) {
  return run(storeName, 'readonly', (store) => store.index(indexName).getAll(key)).then(
    (rows) => rows || []
  );
}

/**
 * The **primary keys** of the records whose indexed field equals `key`.
 *
 * The cheap counterpart to {@link getAllBy}: IndexedDB answers from the index
 * alone, so not one row is deserialized. That is the difference between "how many
 * topics does this chapter have" and pulling every one of their thumbnails into
 * memory to find out (see `study/progress.js`).
 */
export function getAllKeysBy(storeName, indexName, key) {
  return run(storeName, 'readonly', (store) => store.index(indexName).getAllKeys(key)).then(
    (keys) => keys || []
  );
}

/** A single record by key, or `undefined` if it doesn't exist. */
export function get(storeName, key) {
  return run(storeName, 'readonly', (store) => store.get(key));
}

/** Insert or overwrite a record (keyed by its `id`). */
export function put(storeName, value) {
  return run(storeName, 'readwrite', (store) => store.put(value));
}

/** Delete a record by key. */
export function remove(storeName, key) {
  return run(storeName, 'readwrite', (store) => store.delete(key));
}

/**
 * Empty a whole store.
 *
 * Only the restore path needs this (`study/userData.js`): putting a backup back
 * means *replacing* what is there, not merging into it. Nothing in the content
 * cache is ever cleared this way — those stores are reconciled row by row.
 */
export function clear(storeName) {
  return run(storeName, 'readwrite', (store) => store.clear());
}
